import {pnpolyArray} from "../../../../../lib/pnpoly";
import { v4 as uuidv4 } from 'uuid';
import clone from 'lodash/clone';

import EventManager from "../../../store/sqftEventManager";
import workspace from "../../workspace";
import sqftConfig from "../../../store/sqftConfig";
import {
  distanceToInches,
  distanceToMillimeters,
  getAngleBetweenTwoPoints, getMidPoint,
  isDisabledInPopupWithParents,
  measureDistanceToInches,
} from "../../../../../helpers/sqftHelper";
import COLOR_THEMES, {COLOR} from "../../../store/colorThemes";
import {isDisabledInPopupShouldBeHidden} from "../../../../../constants";
import {DIRECTIONS, getLineDirection} from "../../../../../helpers/shapeHelper";
import RenderFigures from "../../../helpers/renderFigures";
import {createAdjustableInput} from "../../../../../helpers/uiHelper";

export default class NumberText {
  id = null;
  design = {
    fontColor: COLOR_THEMES[sqftConfig.activeTheme].primary,
    fontFamily: 'playbold',
    fontHeight: 16,
    isLength: false,
    valuePrefix: '',
    valuePostfix: '',
    shouldRenderFrameBorder: false,
    eventsPriority: EventManager.priorities.SEGMENT
  };
  value = 0;
  callback = null;
  pos = {
    x: 0,
    y: 0,
    xOffset: 0,
    yOffset: 0,
    angle: 0,
    orientation: DIRECTIONS.EAST,
    betweenPoints: {
      pointFrom: null,
      pointTo: null,
    }
  };
  state = {
    isHovered: false,
    mouseDown: false,
    mouseDownPosition: null,
    isHidden: true
  };

  constructor(value, pos, design, parent = workspace, callback = Function(), isInit = true) {
    this.value = value;
    Object.assign(this.pos, pos);
    Object.assign(this.design, design);
    this.parent = parent;
    this.callback = callback;
    this.id = uuidv4();

    if (isInit) {
      this.initEventListeners(design.shouldOnlyRenderText);
    }
  }
  getParent() {
    return this.parent
  }

  get orientation () {
    return this.pos.orientation
  }

  initEventListeners(shouldOnlyRenderText) {
    EventManager.addEvent(
      EventManager.events.RENDER,
      this.render.bind(this),
      this,
      this.design.eventsPriority
    );
    if (!shouldOnlyRenderText) {
      EventManager.addEvent(
        EventManager.events.MOUSE_MOVE,
        this.onMouseMove,
        this,
        this.design.eventsPriority
      );
      EventManager.addEvent(
        EventManager.events.MOUSE_DOWN,
        this.onMouseDown,
        this,
        this.design.eventsPriority
      );
      EventManager.addEvent(
        EventManager.events.MOUSE_UP,
        this.onMouseUp,
        this,
        this.design.eventsPriority
      );
      EventManager.addEvent(
        EventManager.events.MOUSE_LEAVE,
        this.onMouseLeave,
        this,
        EventManager.priorities.OVERALL
      );
    }
  }

  destroy() {
    this.deregisterEvents();
  }

  hideEvents() {
    if (!this.state.isHidden) {
      this.deregisterEvents();
      this.state.isHidden = true;
    }
  }

  revealEvents() {
    if (this.state.isHidden) {
      this.initEventListeners();
      this.state.isHidden = false;
    }
  }

  deregisterEvents() {
    EventManager.removeEvent(this);
  }

  reInitEvents() {
    this.initEventListeners();
  }

  onMouseMove = (mousePos, e) => {
    void e;
    this.state.isHovered = this.isMouseOverText(mousePos);

    if (this.state.isHovered) {
      EventManager.stealCursor();
      //todo rotated cursor
      EventManager.setCursor(([DIRECTIONS.WEST, DIRECTIONS.EAST].includes(this.pos.orientation)) ? "text" : "text-vertical", this);
    } else {
      EventManager.resetCursor(this);
      return false;
    }
  };

  isMouseOverText = (mousePos) => {
    return pnpolyArray(this.getTextPointsArea(), mousePos);
  };

  getTextPointsArea = () => {
    const mText = EventManager.getCtx().measureText(this.design.valuePrefix + this.value + this.design.valuePostfix );
    const {x, y} = this.getPos();

    let pointsArray = [];

    if ([DIRECTIONS.WEST, DIRECTIONS.EAST].includes(this.pos.orientation)) {
      pointsArray = [
        [x - Math.ceil10(mText.width,-2) / 2, y - this.design.fontHeight / 2],
        [x + Math.ceil10(mText.width,-2) / 2, y - this.design.fontHeight / 2],
        [x + Math.ceil10(mText.width,-2) / 2, y + this.design.fontHeight / 2],
        [x - Math.ceil10(mText.width,-2) / 2, y + this.design.fontHeight / 2],
      ]
    } else if ([DIRECTIONS.NORTH, DIRECTIONS.SOUTH].includes(this.pos.orientation)) {
      pointsArray = [
        [x - this.design.fontHeight / 2, y - Math.ceil10(mText.width,-2) / 2],
        [x + this.design.fontHeight / 2, y - Math.ceil10(mText.width,-2) / 2],
        [x + this.design.fontHeight / 2, y + Math.ceil10(mText.width,-2) / 2],
        [x - this.design.fontHeight / 2, y + Math.ceil10(mText.width,-2) / 2],
      ]
    } else {
      // todo horizontal
    }

    return pointsArray;
  };

  onMouseUp = (mousePos, e) => {
    void mousePos;
    void e;
    if (this.state.mouseDown) {
      this.state.mouseDown = false;
      this.state.mouseDownPosition = null;
    }
  };

  onMouseDown = (mousePos, e) => {
    void mousePos;
    void e;
    if (this.state.isHovered && !document.querySelector(".line-input")) {
      window.lineInputFocused = true;
      EventManager.stealCursor();

      const maxLength = this.parent.getMaxLength ? this.parent.getMaxLength() : 1000 * sqftConfig.getMeasureMultiplier();
      const pos = this.getPos();
      const initialValue = this.design.isLength
        ? sqftConfig.measureUnit === "Metric"
          ? measureDistanceToInches(this.value).millimeters
          : this.value
        : this.value;

      const inputWrapper = createAdjustableInput({
        position: pos,
        initialValue: initialValue.toString(),
        min: 1,
        max: maxLength,
        orientation: [DIRECTIONS.WEST, DIRECTIONS.EAST].includes(this.pos.orientation) ? "horizontal" : "vertical",
        onChange: this.onInputChange, // Trigger onInputChange when value changes
        onClose: () => {
          window.lineInputFocused = false;
          this.state.isHovered = false;
        },
        step: 1,
      });

      document.querySelector(".sqft-main-grid").appendChild(inputWrapper);
      // Focus and select input on creation
      const input = inputWrapper.querySelector(".line-input-field");
      input.focus();
    }

    return false;
  };

  onInputChange = (value) => {
    const valueBeforeUpdate = this.value;
    this.value = value;
    this.callback(value, this, valueBeforeUpdate);
  };

  onMouseLeave = () => {
    this.state.isHoveringPoint = false;
    this.state.mouseDown = false;
    this.state.hoveredPoint = null;
    this.state.isHoveredOverText = false;
  };

  setValue = (value) => {
    this.value = value
  }

  setPos = (pos) => {
    this.pos.x = pos.x
    this.pos.y = pos.y
  }

  setAngle = (angle) => {
    this.pos.angle = angle
  }

  setBetweenPoints = (pointFrom, pointTo) => {
    this.pos.betweenPoints.pointFrom = pointFrom;
    this.pos.betweenPoints.pointTo = pointTo;
  }

  getPos = () => {
    const parentOffset = this.parent.getOffset() ? this.parent.getOffset() : { x: 0, y: 0 };
    const scale = clone(sqftConfig.scale);
    let angle = this.pos.angle;

    //if this in the NumberText between lines than we override angle
    if (this.pos.betweenPoints.pointFrom) {
      angle = getAngleBetweenTwoPoints(this.pos.betweenPoints.pointFrom, this.pos.betweenPoints.pointTo);
    }

    const angleRad = angle * (Math.PI / 180);

    // calculate offset position according to offset point
    const calculatedXOffset = this.pos.xOffset * Math.cos(angleRad) - this.pos.yOffset * Math.sin(angleRad);
    const calculatedYOffset = this.pos.xOffset * Math.sin(angleRad) + this.pos.yOffset * Math.cos(angleRad);

    return {
      x: this.pos.x * scale + calculatedXOffset + parentOffset.x,
      y: this.pos.y * scale + calculatedYOffset + parentOffset.y
    }
  };

  getValue = (isString, withSuffixes = false) => {
    let value = this.value;
    if (this.design.isLength) {
      value = sqftConfig.measureUnit === "Metric"
        ? distanceToMillimeters(value)
        : distanceToInches(value)
    } else if (isString) {
      value = value.toString();
    }
    if (withSuffixes) {
      value = this.design.valuePrefix + value.toString() + this.design.valuePostfix;
    }

    return value
  };

  updateOrientation = () => {
    if (this.pos.betweenPoints.pointFrom) {
      this.pos.orientation = getLineDirection(this.pos.betweenPoints.pointFrom, this.pos.betweenPoints.pointTo, true);
    }
  }

  updatePositionBetweenPoints = () => {
    if (this.pos.betweenPoints.pointFrom) {
      const pointBetweenPoints = getMidPoint(this.pos.betweenPoints.pointFrom, this.pos.betweenPoints.pointTo);

      this.pos.x = pointBetweenPoints.x
      this.pos.y = pointBetweenPoints.y
    }
  }

  render = (ctx) => {
    if ((isDisabledInPopupShouldBeHidden() && isDisabledInPopupWithParents(this))
      || (this.pos.x === 0 && this.pos.y === 0) // this is for Free Draw. see https://qcquotecountertops.atlassian.net/browse/QCDES-2064
    ) {
      return
    }

    this.updateOrientation();
    this.updatePositionBetweenPoints();
    if (this.design.shouldRenderFrameBorder) {
      this.renderFrameBorder(ctx);
    }
    this.renderText(ctx);
  }

  renderFrameBorder = (ctx) => {
    ctx.beginPath();
    const pos = this.getPos();
    const value = this.getValue(true, true);
    const mText = ctx.measureText(value);
    let angle = 0;
    if (this.pos.betweenPoints.pointFrom) {
      angle = getAngleBetweenTwoPoints(this.pos.betweenPoints.pointFrom, this.pos.betweenPoints.pointTo);
      //this will help to rotate upside down text
      if ([DIRECTIONS.NORTHWEST, DIRECTIONS.SOUTHWEST].includes(this.pos.orientation)) {
        angle += 180;
      }
    }

    ctx.strokeStyle = COLOR_THEMES[sqftConfig.activeTheme].primary;
    ctx.lineWidth = 2;
    ctx.fillStyle = COLOR.white

    ctx.save();
    ctx.translate(
      pos.x ,
      pos.y
    );
    ctx.rotate(angle * (Math.PI / 180));
    RenderFigures.roundedRect(ctx,
      - mText.width / 2, // / sqftConfig.scale * sqftConfig.defaultScale,
      - this.design.fontHeight / 1.5,
      mText.width * 1.5, /// sqftConfig.scale * sqftConfig.defaultScale,
      this.design.fontHeight + 6,
      5,
      true,
      true
    );

    ctx.restore();
    ctx.closePath();
  }

  renderText = (ctx) => {
    const //scale = sqftConfig.scale,
      fontHeight = this.design.fontHeight, //getFontsizeSmall(),
      font = fontHeight + "px " + this.design.fontFamily,
      value = this.getValue(true, true),//this.value.toString(),

      mText = ctx.measureText(value),
      pos = this.getPos();
    let angle = this.pos.angle;
    ctx.beginPath();
    ctx.font = font;
    ctx.textAlign = "center";
    ctx.fillStyle = this.design.fontColor;
    if (this.pos.betweenPoints.pointFrom) {
      angle = getAngleBetweenTwoPoints(this.pos.betweenPoints.pointFrom, this.pos.betweenPoints.pointTo);
    }

    switch (this.orientation) {
      case DIRECTIONS.EAST:
      case DIRECTIONS.WEST:
      case DIRECTIONS.SOUTH:
      case DIRECTIONS.NORTH:
      case DIRECTIONS.SOUTHEAST:
      case DIRECTIONS.SOUTHWEST:
      case DIRECTIONS.NORTHEAST:
      case DIRECTIONS.NORTHWEST: {
        //this will help to rotate upside down text
        if (this.pos.betweenPoints.pointFrom) {
          if ([DIRECTIONS.NORTHWEST, DIRECTIONS.SOUTHWEST, DIRECTIONS.WEST].includes(this.pos.orientation)) {
            angle += 180;
          }
        }

        ctx.save();
        ctx.translate(pos.x, pos.y);
        ctx.rotate(angle * (Math.PI / 180));
        ctx.wrapText(value, fontHeight / 2, fontHeight / 3, mText, fontHeight);
        ctx.restore();

        //ctx.wrapText(value, pos.x, pos.y, mText, fontHeight);
        break;
      }
      default:
        break;
    }
  }
}
